import React, { useState, useEffect } from 'react';

const DISMISS_KEY = 'nel_pwa_install_dismissed';
const DISMISS_DAYS = 7;

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    /* Don't show if already installed */
    if (isInStandaloneMode()) return;

    /* Don't show if dismissed recently */
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400_000) return;

    if (isIOS()) {
      /* iOS doesn't support beforeinstallprompt — show manual guide */
      setShowIOSGuide(true);
      setVisible(true);
      return;
    }

    const handler = e => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  const bannerStyle = {
    position:   'fixed',
    bottom:     72, /* above mobile bottom nav */
    left:       12,
    right:      12,
    zIndex:     8888,
    background: '#1B2A6B',
    border:     '1px solid rgba(201,168,76,0.4)',
    borderRadius: 14,
    padding:    '14px 16px',
    display:    'flex',
    alignItems: 'center',
    gap:        12,
    boxShadow:  '0 8px 24px rgba(0,0,0,0.3)',
  };

  return (
    <div style={bannerStyle} role="complementary" aria-label="Install app banner">
      {/* Logo */}
      <div style={{
        width: 40, height: 40, background: '#C9A84C', borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 17, color: '#1B2A6B', flexShrink: 0,
      }}>N</div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 13.5 }}>Install NEL CRM</div>
        {showIOSGuide ? (
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, marginTop: 2 }}>
            Tap <strong style={{ color: '#C9A84C' }}>Share</strong> → <strong style={{ color: '#C9A84C' }}>Add to Home Screen</strong>
          </div>
        ) : (
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, marginTop: 2 }}>
            Get the full app experience — works offline
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {!showIOSGuide && (
          <button
            onClick={install}
            style={{
              background: '#C9A84C', color: '#1B2A6B', border: 'none',
              borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
            }}
          >Install</button>
        )}
        <button
          onClick={dismiss}
          style={{
            background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', border: 'none',
            borderRadius: 8, padding: '7px 10px', fontSize: 12, cursor: 'pointer',
          }}
        >{showIOSGuide ? 'Got it' : 'Not now'}</button>
      </div>
    </div>
  );
}
